// src/app/modules/demandes/DemanderFormation.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import "../../RessourcesStyles/DemanderFormation.css";
import DemandeFormModal from "./DemandeFormModal";

import {
    Search,
    Filter,
    ChevronDown,
    ChevronLeft,
    Plus,
    Info,
    Eye,
    Download,
    BookOpen,
    Sparkles,
    Layers,
    Bookmark,
    Calendar,
    X,
    Target,
    ClipboardCheck,
    Users,
    Clock,
    BarChart,
    ChevronUp,
} from "lucide-react";
import { fetchVisibleCampagnesRequest } from "../../../../../redux/actions/campagnesActions";
import { fetchCataloguesRequest } from "../../../../../redux/actions/cataloguesActions";
import { fetchFormationsByCatalogueRequest } from "../../../../../redux/actions/formationsActions";
import { createDemandeCollabRequest, getMyFormationDemandesRequest } from "../../../../../redux/actions/demandesActions";
import HorsCatalogue from "./HorsCatalogue";
import {API_BASE_URL} from "../../../../../../api";
import {
    fetchFormationBookmarksRequest,
    toggleFormationBookmarkRequest,
} from "../../../../../redux/actions/formationBookmarksActions";

const isFormationCampaign = (c) => String(c?.typeCampagne || "").toUpperCase() === "FORMATION";
const isCampaignActive = (c) => c?.status === true;
const isDemandeInactive = (status) => ["REJECTED", "CANCELLED", "WITHDRAWN"].includes(String(status || "").toUpperCase());

export default function DemanderFormation({
                                              campagneId: campagneIdProp = null,
                                              hideCampagneSelect = false,
                                          }) {
    const dispatch = useDispatch();

    const { visibleList: campagnesRaw = [], loading: campagnesLoading } = useSelector((s) => s.campagnes || {});
    const { catalogues = [], loading: cataloguesLoading } = useSelector((s) => s.catalogues || {});
    const { list: formations = [], listByCatalogue = {}, loading: formationsLoading } = useSelector((s) => s.formations || {});
    const { myFormationDemandes = [] } = useSelector((s) => s.demandes || {});
    const { bookmarksByCampagneId = {} } = useSelector((s) => s.formationBookmarks || {});
    const campagnes = useMemo(() => (campagnesRaw || []).filter(isFormationCampaign), [campagnesRaw]);

    // si pas piloté par parent, fallback local
    const [campagneIdLocal, setCampagneIdLocal] = useState("");
    const campagneId = campagneIdProp ?? (campagneIdLocal ? Number(campagneIdLocal) : null);

    // UI
    const [openHorsCatalogueModal, setOpenHorsCatalogueModal] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [search, setSearch] = useState("");
    const [catalogueId, setCatalogueId] = useState("all");

    // Pagination formations
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 8;

    // Demande modal
    const [selectedFormation, setSelectedFormation] = useState(null);
    const [openModal, setOpenModal] = useState(false);

    // Details modal (visualisation)
    const [detailsFormation, setDetailsFormation] = useState(null);
    const [openDetails, setOpenDetails] = useState(false);
    const [recommendationTab, setRecommendationTab] = useState("toDevelop"); // "toDevelop" | "inProgress"
    const [showScrollTop, setShowScrollTop] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
            setShowScrollTop(scrollTop > 100);
        };
        window.addEventListener("scroll", handleScroll, true); // 'true' pour capturer le scroll des conteneurs enfants
        return () => window.removeEventListener("scroll", handleScroll, true);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };
    useEffect(() => {
        dispatch(fetchVisibleCampagnesRequest());
        dispatch(fetchCataloguesRequest());
        dispatch(getMyFormationDemandesRequest());
    }, [dispatch]);

    useEffect(() => {
        if (campagneId) {
            dispatch(fetchFormationBookmarksRequest(campagneId));
        }
    }, [dispatch, campagneId]);

    // auto select campagne (uniquement si pas de parent)
    useEffect(() => {
        if (campagneIdProp != null) return;
        if (!campagneIdLocal && campagnes?.length) {
            const defaultCampagne = campagnes.find((c) => c?.isDefault) || campagnes[0];
            setCampagneIdLocal(String(defaultCampagne.id));
        }
    }, [campagnes, campagneIdLocal, campagneIdProp]);

    const selectedCampagne = useMemo(() => {
        if (!campagneId) return null;
        return (campagnes || []).find((c) => String(c.id) === String(campagneId));
    }, [campagnes, campagneId]);

    const activeDemandesForCampagne = useMemo(
        () => (myFormationDemandes || []).filter((d) => String(d?.campagneId) === String(campagneId) && !isDemandeInactive(d?.status)),
        [myFormationDemandes, campagneId]
    );

    const hasReachedQuota = useMemo(() => {
        if (!selectedCampagne?.nombreMaxFormations) return false;
        return activeDemandesForCampagne.length >= Number(selectedCampagne.nombreMaxFormations);
    }, [activeDemandesForCampagne.length, selectedCampagne]);

    const requestedFormationIds = useMemo(
        () => new Set(activeDemandesForCampagne.map((d) => String(d?.formationId)).filter(Boolean)),
        [activeDemandesForCampagne]
    );

    const bookmarkedFormationIds = useMemo(
        () => new Set((bookmarksByCampagneId[String(campagneId)] || []).map((b) => String(b?.formationId || b?.id)).filter(Boolean)),
        [bookmarksByCampagneId, campagneId]
    );

    // catalogues visibles selon campagne.catalogueIds
    const visibleCatalogues = useMemo(() => {
        if (!selectedCampagne?.catalogueIds?.length) return [];
        const setIds = new Set(selectedCampagne.catalogueIds.map((x) => String(x)));
        return (catalogues || []).filter((cat) => setIds.has(String(cat.id)));
    }, [catalogues, selectedCampagne]);

    // reset catalogue si campagne change
    useEffect(() => {
        if (campagneIdProp) setCampagneIdLocal(String(campagneIdProp));
    }, [campagneIdProp]);

    // On ne reset QUE si la campagne change vraiment
    const lastCampagneId = useRef(campagneId);
    useEffect(() => {
        if (campagneId !== lastCampagneId.current) {
            setCatalogueId("all");
            setSearch("");
            setCurrentPage(1);
            lastCampagneId.current = campagneId;
        }
    }, [campagneId]);



    // charger formations via endpoint by catalogue
    const loadedCataloguesRef = useRef(new Set());

    useEffect(() => {
        if (!campagneId) return;

        const loadCatalogue = (id) => {
            const key = String(id);

            if (loadedCataloguesRef.current.has(key)) return;
            if (listByCatalogue?.[id]?.length > 0) {
                loadedCataloguesRef.current.add(key);
                return;
            }

            loadedCataloguesRef.current.add(key);
            dispatch(fetchFormationsByCatalogueRequest(id));
        };

        if (catalogueId === "all" || catalogueId === "recommendations") {
            visibleCatalogues.forEach((cat) => loadCatalogue(cat.id));
            return;
        }

        if (catalogueId && !isNaN(catalogueId)) {
            loadCatalogue(catalogueId);
        }
    }, [dispatch, campagneId, catalogueId, visibleCatalogues]);

    useEffect(() => {
        loadedCataloguesRef.current.clear();
    }, [campagneId]);
    // reset page quand filtre/search change
    useEffect(() => {
        setSearch("");
        setCurrentPage(1);
    }, [catalogueId]);

    // Aggregation de toutes les formations des catalogues visibles si catalogueId === "all"
    const allFormations = useMemo(() => {
        if (catalogueId !== "all" && catalogueId !== "recommendations") return formations || [];

        const aggregated = [];
        const seen = new Set();

        visibleCatalogues.forEach(cat => {
            const list = listByCatalogue[cat.id] || [];
            list.forEach(f => {
                if (!seen.has(f.id)) {
                    seen.add(f.id);
                    aggregated.push(f);
                }
            });
        });
        return aggregated;
    }, [catalogueId, formations, listByCatalogue, visibleCatalogues]);

    // Mocked Recommendations (Prend les 3 premières formations pour la démo)
    const recommendedFormations = useMemo(() => {
        return []; // Vide par défaut selon la demande
    }, []);

    const displayFormations = useMemo(() => {
        if (catalogueId === "recommendations") return recommendedFormations || [];
        if (catalogueId === "all") return allFormations || [];
        return listByCatalogue[catalogueId] || [];
    }, [catalogueId, allFormations, recommendedFormations, listByCatalogue]);

    const filteredFormations = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return displayFormations;
        return displayFormations.filter((f) => {
            const name = String(f?.nom || f?.name || "").toLowerCase();
            const ref = String(f?.reference || "").toLowerCase();
            const desc = String(f?.description || "").toLowerCase();
            return name.includes(q) || ref.includes(q) || desc.includes(q);
        });
    }, [displayFormations, search]);

    const totalPages = Math.ceil(filteredFormations.length / itemsPerPage) || 1;

    const paginatedFormations = useMemo(() => {
        return filteredFormations.slice(
            (currentPage - 1) * itemsPerPage,
            currentPage * itemsPerPage
        );
    }, [filteredFormations, currentPage]);

    useEffect(() => {
        const total = Math.ceil(filteredFormations.length / itemsPerPage) || 1;
        if (currentPage > total) {
            setCurrentPage(total);
        }
    }, [filteredFormations.length, currentPage]);

    const renderPagination = ({ totalItems, currentPage, setCurrentPage, label }) => {
        const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

        if (!totalItems || totalItems <= itemsPerPage) return null;

        return (
            <div className="cicada-pagination">
                <div className="cicada-pagination-info">
                    Affichage de {(currentPage - 1) * itemsPerPage + 1}{' '}
                    à {Math.min(currentPage * itemsPerPage, totalItems)}{' '}
                    sur {totalItems} {label}
                </div>

                <div className="cicada-pagination-actions">
                    <button
                        type="button"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    >
                        Précédent
                    </button>

                    <span>
                        Page {currentPage} / {totalPages}
                    </span>

                    <button
                        type="button"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    >
                        Suivant
                    </button>
                </div>
            </div>
        );
    };

    const selectedCatalogue = useMemo(() => {
        if (catalogueId === "all") return null;
        return (visibleCatalogues || []).find((c) => String(c.id) === String(catalogueId)) || null;
    }, [visibleCatalogues, catalogueId]);

    // 👉 adapte ces clés à ton backend si besoin
    const getCataloguePdfUrl = (cat) =>
        cat?.pdfUrl || cat?.pdfLink || cat?.cataloguePdfUrl || cat?.urlPdf || cat?.lienPdf || cat?.pdf || "";

    const getFormationRequestState = (formationId) => {
        const alreadyRequested = requestedFormationIds.has(String(formationId));
        const inactiveCampaign = !isCampaignActive(selectedCampagne);
        const disabled = alreadyRequested || hasReachedQuota || inactiveCampaign;
        const label = alreadyRequested
            ? "Déjà demandée"
            : hasReachedQuota
                ? "Quota atteint"
                : inactiveCampaign
                    ? "Campagne inactive"
                    : "Demander";

        return { disabled, label };
    };

    const openDemande = (formation) => {
        if (!campagneId) {
            Swal.fire({ icon: "warning", text: "Sélectionne d’abord une campagne." });
            return;
        }
        if (!isCampaignActive(selectedCampagne)) {
            Swal.fire({ icon: "warning", text: "Cette campagne n'est pas active." });
            return;
        }
        if (requestedFormationIds.has(String(formation?.id))) {
            return;
        }
        if (hasReachedQuota) {
            Swal.fire({
                icon: "warning",
                text: "Vous avez atteint le nombre maximum de demandes autorisées pour cette campagne",
            });
            return;
        }
        setSelectedFormation(formation);
        setOpenModal(true);
    };

    const openFormationDetails = (formation) => {
        console.log("formation details:", formation); // ← ajoute ça
        setDetailsFormation(formation);
        setOpenDetails(true);
    };

    const toggleBookmark = (event, formation) => {
        event?.stopPropagation();
        if (!campagneId) {
            Swal.fire({ icon: "warning", text: "Sélectionne d'abord une campagne." });
            return;
        }
        dispatch(toggleFormationBookmarkRequest({
            campagneId,
            formationId: formation?.id,
            formation,
        }));
    };

    const submitDemande = (payloadFromModal) => {
        if (!campagneId) {
            Swal.fire({ icon: "warning", text: "Campagne obligatoire." });
            return;
        }

        const payload = {
            ...payloadFromModal,
            campagneId,
            formationId: selectedFormation?.id || payloadFromModal.formationId,
            type: "FORMATION",
        };

        dispatch(createDemandeCollabRequest(payload));
        setOpenModal(false);
        setSelectedFormation(null);
    };

    const loading = !!(campagnesLoading || cataloguesLoading || formationsLoading);

    return (
        <div className="dfPage">
            <div className="dfWrap">
                <div className="dfHeader">
                    <div>
                        <h1 className="dfTitle">Demande de formation</h1>
                    </div>
                    <div className="dfHeaderActions">
                        <span className="dfHeaderHint">Vous ne trouvez pas la formation que vous cherchez ?</span>
                        <button 
                            type="button"
                            className="dfBtn dfOutCatalogBtn"
                            onClick={() => setOpenHorsCatalogueModal(true)}
                        >
                            <Plus size={16} />
                            Demande hors catalogue
                        </button>
                    </div>
                </div>

                {!hideCampagneSelect && (
                    <div className="dfCampaignBar">
                        <label className="dfCampaignLabel">Campagne de formation</label>
                        <div className="dfSelectWrapper">
                            <Calendar className="dfSelectIcon" size={16} />
                            <select
                                className="dfSelect"
                                value={campagneIdLocal}
                                onChange={(e) => {
                                    setCampagneIdLocal(e.target.value);
                                    setCurrentPage(1);
                                }}
                            >
                                <option value="">— Sélectionnez une campagne —</option>
                                {campagnes.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.nom || c.name} {c.anneeCampagne ? `(${c.anneeCampagne})` : ''}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}

                <div className="dfCard">
                    {!campagneId ? (
                        <div className="ae-empty">
                            <Calendar size={40} className="ae-empty-icon" />
                            <h3>Aucune campagne active</h3>
                            <p>Veuillez sélectionner une campagne de formation ci-dessus pour accéder au catalogue.</p>
                        </div>
                    ) : (
                        <div className="dfMainContent">
                            <div className="dfSubTabs">
                                <button
                                    type="button"
                                    className={`dfSubTab ${catalogueId === "all" ? "is-active" : ""}`}
                                    onClick={() => setCatalogueId("all")}
                                >
                                    <Layers size={14} style={{marginRight: 6}}/>
                                    Toutes les formations
                                </button>
                                <button
                                    type="button"
                                    className={`dfSubTab ${catalogueId === "recommendations" ? "is-active" : ""}`}
                                    onClick={() => setCatalogueId("recommendations")}
                                >
                                    <Sparkles size={14} style={{marginRight: 6}}/>
                                    Recommandations
                                </button>
                            </div>

                            <div className="dfControls">
                                <div className="dfSearch">
                                    <Search size={16}/>
                                    <input
                                        value={search}
                                        onChange={(e) => {
                                            setSearch(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                        placeholder="Quelle formation recherchez-vous ?"
                                    />
                                    {search && <X size={16} className="dfSearchClear" onClick={() => { setSearch(""); setCurrentPage(1); }}/>}
                                </div>
                            </div>

                            <div className="dfContentArea">
                                {loading && filteredFormations.length === 0 ? (
                                    <div className="dfEmpty">Chargement des formations...</div>
                                ) : (
                                    <div className="dfCataloguesList">
                                    {String(catalogueId) === "recommendations" ? (
                                            <div className="dfRecContainer">
                                                <div className="dfRecContent">
                                                    <div className="dfDevelopmentNotice">
                                                        <Sparkles size={24} style={{ color: '#204385', marginBottom: 10 }} />
                                                        <p>Aucune recommendation pour le moment</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (catalogueId === "all" || visibleCatalogues.length === 1) ? (
                                            <>
                                                {visibleCatalogues.length === 1 && (
                                                    <div className="dfSingleCatMeta">
                                                        <span>{filteredFormations.length} formations disponibles</span>
                                                        <a
                                                            className="dfLink"
                                                            href={getCataloguePdfUrl(visibleCatalogues[0]) || undefined}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >
                                                            Télécharger le catalogue
                                                        </a>
                                                    </div>
                                                )}
                                                <div className="dfFormationGrid">
                                                    {paginatedFormations.map((f) => (
                                                        <div className="dfFormationCard" key={f.id}>
                                                            <div className="dfFormationMedia" onClick={() => openFormationDetails(f)}>
                                                                {f.picture ? (
                                                                    <img
                                                                        src={`${API_BASE_URL}/api/formations/images/${f.picture}`}
                                                                        alt={f.nom}
                                                                    />
                                                                ) : (
                                                                    <div className="dfFormationPlaceholder">
                                                                        {(f.nom || "F").slice(0, 1).toUpperCase()}
                                                                    </div>
                                                                )}
                                                                <div className="dfFormationOverlay">
                                                                    <span className="dfOverlayBtn">Détails</span>
                                                                </div>
                                                            </div>

                                                            <div className="dfFormationBody">
                                                                <div className="dfFormationTitle" title={f.nom}>{f.nom}</div>

                                                                <div className="dfFormationInfo">
                                                                    <div className="dfInfoRow"><span>Domaine :</span> {f.domaineNom || f.domaine?.nom || "—"}</div>
                                                                    <div className="dfInfoRow"><span>Thème :</span> {f.themeNom || f.theme?.nom || "—"}</div>
                                                                    <div className="dfInfoRow"><span>Mode :</span> {f.type || "—"}</div>
                                                                    <div className="dfInfoRow"><span>Durée :</span> {f.duration ? `${f.duration} ${f.typeDuree || 'jour(s)'}` : "—"}</div>
                                                                </div>

                                                                <div className="dfFormationFooter">
                                                                    <button
                                                                        type="button"
                                                                        className="dfSubmitBtn"
                                                                        disabled={getFormationRequestState(f.id).disabled}
                                                                        onClick={() => openDemande(f)}
                                                                    >
                                                                        {getFormationRequestState(f.id).label}
                                                                    </button>
                                                                    <div className="dfCardActions">
                                                                        <button
                                                                            type="button"
                                                                            className={`dfActionBtn ${bookmarkedFormationIds.has(String(f.id)) ? "is-bookmarked" : ""}`}
                                                                            title="Sauvegarder"
                                                                            onClick={(event) => toggleBookmark(event, f)}
                                                                        >
                                                                            <Bookmark size={18} fill={bookmarkedFormationIds.has(String(f.id)) ? "currentColor" : "none"} />
                                                                        </button>
                                                                        <button type="button" className="dfActionBtn" onClick={() => openFormationDetails(f)} title="Détails"><Eye size={18} /></button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        ) : (
                                            visibleCatalogues
                                                .filter(cat => String(cat.id) === String(catalogueId))
                                                .map((cat) => {
                                                    const catFormations = (listByCatalogue[cat.id] || []).filter(f =>
                                                        filteredFormations.some(ff => ff.id === f.id)
                                                    );
                                                    const paginatedCatFormations = catFormations.slice(
                                                        (currentPage - 1) * itemsPerPage,
                                                        currentPage * itemsPerPage
                                                    );

                                                    return (
                                                        <div className="dfCatalogueGroup" key={cat.id}>
                                                            <div className="dfCatalogueHeader">
                                                                <div className="dfCatalogueInfo">
                                                                    <div className="dfCatalogueTitle">{cat.nom || cat.name}</div>
                                                                    <div className="dfCatalogueCount">{catFormations.length} formation{catFormations.length > 1 ? "s" : ""}</div>
                                                                </div>
                                                                <div className="dfCatalogueActions">
                                                                    <a
                                                                        className={`dfIconBtn ${getCataloguePdfUrl(cat) ? "" : "is-disabled"}`}
                                                                        title="PDF"
                                                                        href={getCataloguePdfUrl(cat) || undefined}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                    >
                                                                        <Download size={16} />
                                                                    </a>
                                                                </div>
                                                            </div>

                                                            {catFormations.length === 0 ? (
                                                                <div className="dfNoFormations">Aucune formation correspondante.</div>
                                                            ) : (
                                                                <div className="dfFormationGrid">
                                                                    {paginatedCatFormations.map((f) => (
                                                                        <div className="dfFormationCard" key={f.id}>
                                                                            <div className="dfFormationMedia" onClick={() => openFormationDetails(f)}>
                                                                                {f.picture ? (
                                                                                    <img
                                                                                        src={`${API_BASE_URL}/api/formations/images/${f.picture}`}
                                                                                        alt={f.nom}
                                                                                    />
                                                                                ) : (
                                                                                    <div className="dfFormationPlaceholder">
                                                                                        {(f.nom || "F").slice(0, 1).toUpperCase()}
                                                                                    </div>
                                                                                )}
                                                                                <div className="dfFormationOverlay">
                                                                                    <span className="dfOverlayBtn">Détails</span>
                                                                                </div>
                                                                            </div>

                                                                            <div className="dfFormationBody">
                                                                                <div className="dfFormationTitle" title={f.nom}>{f.nom}</div>

                                                                                <div className="dfFormationInfo">
                                                                                    <div className="dfInfoRow"><span>Domaine :</span> {f.domaineNom || f.domaine?.nom || "—"}</div>
                                                                                    <div className="dfInfoRow"><span>Thème :</span> {f.themeNom || f.theme?.nom || "—"}</div>
                                                                                    <div className="dfInfoRow"><span>Mode :</span> {f.type || "—"}</div>
                                                                                    <div className="dfInfoRow"><span>Durée :</span> {f.duration ? `${f.duration} ${f.typeDuree || 'jour(s)'}` : "—"}</div>
                                                                                </div>

                                                                                <div className="dfFormationFooter">
                                                                                    <button
                                                                                        type="button"
                                                                                        className="dfSubmitBtn"
                                                                                        disabled={getFormationRequestState(f.id).disabled}
                                                                                        onClick={() => openDemande(f)}
                                                                                    >
                                                                                        {getFormationRequestState(f.id).label}
                                                                                    </button>
                                                                                    <div className="dfCardActions">
                                                                                    <button
                                                                                        type="button"
                                                                                        className={`dfActionBtn ${bookmarkedFormationIds.has(String(f.id)) ? "is-bookmarked" : ""}`}
                                                                                        title="Sauvegarder"
                                                                                        onClick={(event) => toggleBookmark(event, f)}
                                                                                    >
                                                                                        <Bookmark size={18} fill={bookmarkedFormationIds.has(String(f.id)) ? "currentColor" : "none"} />
                                                                                    </button>
                                                                                        <button type="button" className="dfActionBtn" onClick={() => openFormationDetails(f)} title="Détails"><Eye size={18} /></button>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })
                                        )}
                                        {renderPagination({
                                            totalItems: filteredFormations.length,
                                            currentPage,
                                            setCurrentPage,
                                            label: "formations"
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {openHorsCatalogueModal && (
                <div className="tcvModal">
                    <div className="tcvModal__panel" style={{ width: 'min(800px, 100%)' }}>
                        <div className="tcvModal__header">
                            <div className="tcvModal__headLeft">
                                <div className="tcvModal__icon"><Plus size={18} /></div>
                                <div><div className="tcvModal__title">Demande hors catalogue</div></div>
                            </div>
                            <button type="button" className="tcvIconBtn" onClick={() => setOpenHorsCatalogueModal(false)}><X size={18} /></button>
                        </div>
                        <div className="tcvModal__body" style={{ padding: '20px' }}>
                            <HorsCatalogue campagneId={campagneId} hideCampagneSelect onCancel={() => setOpenHorsCatalogueModal(false)} onSuccess={() => setOpenHorsCatalogueModal(false)} />
                        </div>
                    </div>
                </div>
            )}

            <DemandeFormModal
                open={openModal}
                onClose={() => setOpenModal(false)}
                onSubmit={submitDemande}
                formation={selectedFormation}
                campagneId={campagneId}
                type="FORMATION"
            />

            {openDetails && detailsFormation && (
                <div className="dfDetailsModalOverlay" onMouseDown={(e) => e.target === e.currentTarget && setOpenDetails(false)}>
                    <div className="dfDetailsModal">
                        {/* Header Image Section */}
                        <div className="dfDetailsBanner">
                            {detailsFormation.picture ? (
                                <img src={`${API_BASE_URL}/api/formations/images/${detailsFormation.picture}`} alt={detailsFormation.nom} />
                            ) : (
                                <div className="dfDetailsPlaceholderBanner">
                                    <span>{(detailsFormation.nom || "F").slice(0, 1).toUpperCase()}</span>
                                </div>
                            )}
                            <div className="dfDetailsBadgeTop">{detailsFormation.type || "Formation"}</div>
                            <button type="button" className="dfDetailsClose" onClick={() => setOpenDetails(false)}>
                                <X size={20} />
                            </button>
                        </div>

                        <div className="dfDetailsBody">
                            <h2 className="dfDetailsTitle">{detailsFormation.nom || detailsFormation.name}</h2>
                            <div className="dfDetailsTitleUnderline"></div>

                            <div className="dfDetailsGrid">
                                <div className="dfDetailsMain">
                                    <section className="dfDetailsSection">
                                        <div className="dfSectionHeader">
                                            <div className="dfSectionIcon"><Target size={20} /></div>
                                            <h3>Objectif(s)</h3>
                                        </div>
                                        <ul className="dfDetailsList">
                                            {(() => {
                                                // On récupère directement les 3 champs d'objectifs de la base
                                                const rawFields = [
                                                    detailsFormation.objectif,
                                                    detailsFormation.objectif2,
                                                    detailsFormation.objectif3
                                                ].filter(Boolean);

                                                if (rawFields.length === 0) {
                                                    return <li className="dfNoBullet dfMuted">Aucun objectif spécifié</li>;
                                                }

                                                // On découpe chaque champ s'il contient des retours à la ligne ou des points-virgules
                                                const allPoints = [];
                                                rawFields.forEach(val => {
                                                    const parts = String(val).split(/[\n;•]+/).map(p => p.trim()).filter(Boolean);
                                                    allPoints.push(...parts);
                                                });

                                                return allPoints.map((item, idx) => (
                                                    <li key={idx}>{item}</li>
                                                ));
                                            })()}
                                        </ul>
                                    </section>

                                    <section className="dfDetailsSection">
                                        <div className="dfSectionHeader">
                                            <div className="dfSectionIcon"><ClipboardCheck size={20} /></div>
                                            <h3>Prérequis</h3>
                                        </div>
                                        <ul className="dfDetailsList">
                                            {detailsFormation.prerequis ? (
                                                detailsFormation.prerequis.split(/[\n;]+/).map(p => p.trim()).filter(Boolean).map((p, i) => (
                                                    <li key={i}>{p}</li>
                                                ))
                                            ) : (
                                                <li className="dfNoBullet dfMuted">Aucun prérequis spécifique pour cette formation</li>
                                            )}
                                        </ul>
                                    </section>
                                </div>

                                <aside className="dfDetailsSidebar">
                                    <div className="dfInfoCard">
                                        <h4>Informations pratiques</h4>
                                        <div className="dfInfoItem">
                                            <Users size={18} />
                                            <div>
                                                <label>Public visé</label>
                                                <span>{detailsFormation.publiqueVise || "Tous les collaborateurs"}</span>
                                            </div>
                                        </div>
                                        <div className="dfInfoItem">
                                            <Clock size={18} />
                                            <div>
                                                <label>Durée</label>
                                                <span>{detailsFormation.duration || "—"} {detailsFormation.typeDuree || "jour(s)"}</span>
                                            </div>
                                        </div>
                                        {/*<div className="dfInfoItem">*/}
                                        {/*    <BarChart size={18} />*/}
                                        {/*    <div>*/}
                                        {/*        <label>Niveau(x) disponible(s)</label>*/}
                                        {/*        <span>{detailsFormation.niveaux || "Tous niveaux"}</span>*/}
                                        {/*    </div>*/}
                                        {/*</div>*/}
                                        {/*<div className="dfInfoItem">*/}
                                        {/*    <Calendar size={18} />*/}
                                        {/*    <div>*/}
                                        {/*        <label>Période(s) disponible(s)</label>*/}
                                        {/*        <span>{detailsFormation.periods || "—"}</span>*/}
                                        {/*    </div>*/}
                                        {/*</div>*/}
                                    </div>
                                    <button
                                        type="button"
                                        className="dfDetailsActionBtn"
                                        onClick={() => { setOpenDetails(false); openDemande(detailsFormation); }}
                                    >
                                        Demander cette formation
                                    </button>
                                </aside>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showScrollTop && (
                <button className="dfScrollTop" onClick={scrollToTop} title="Retour en haut">
                    <ChevronUp size={24} />
                </button>
            )}
        </div>
    );
}
